import type { FiMigrationInfo, InstitutionLogin } from '../institution-logins/institutionLoginsTypes';
import { getAggregatorFromChannel } from '../institutions/institutionsUtils';
import type { AccountDiscoveryMode, AggAction } from './accountDiscoveryTypes';
import type { FiMigrationFunnelProperties, FiMigrationProvenance } from './fiMigrationTypes';

export const FI_MIGRATION_LEARN_MORE_URL = 'https://support.simplifi.quicken.com/en/articles/16965088-migration';

export const FORCED_FI_MIGRATION_MENU_LABEL = 'Reconnect institution';

export const isFiMigrationAction = (action: AggAction | null | undefined): boolean => action?.id === 'FI_MIGRATION';

export const isForcedFiMigrationAction = (action: AggAction | null | undefined): boolean =>
  action?.id === 'CHANNEL_RESET_REQUIRED';

// Optional and forced migrations run the same migration flow.
export const isAnyFiMigrationAction = (action: AggAction | null | undefined): boolean =>
  isFiMigrationAction(action) || isForcedFiMigrationAction(action);

export const getForcedFiMigrationTitle = (location: string): string =>
  location === 'account settings' ? 'Attention needed' : 'Account needs attention';

const hasFiMigrationDestination = (destination: FiMigrationInfo | null | undefined): boolean =>
  Boolean(
    destination?.toInstitutionId &&
      destination.toChannel &&
      destination.toInstitutionId !== 'unknown' &&
      destination.toChannel !== 'unknown',
  );

const isActiveLogin = (login: InstitutionLogin | null | undefined): login is InstitutionLogin =>
  Boolean(login?.isConnected && !login.isDeleted);

// Retained metadata is stale once the login reaches its destination.
const hasPendingFiMigrationDestination = (login: InstitutionLogin): boolean => {
  const destination = login.fiMigrationInfo;
  return (
    hasFiMigrationDestination(destination) &&
    (destination?.toInstitutionId !== login.institutionId || destination?.toChannel !== login.channel)
  );
};

export const isForcedFiMigration = (login: InstitutionLogin | null | undefined): boolean =>
  isActiveLogin(login) &&
  (Boolean(login.aggregators?.some((aggregator) => aggregator.aggStatus === 'CHANNEL_RESET_REQUIRED')) ||
    (login.fiMigrationInfo?.fiMigrationType === 'FORCED_MIGRATION' && hasPendingFiMigrationDestination(login)));

export const isOptionalFiMigration = (login: InstitutionLogin | null | undefined): boolean =>
  isActiveLogin(login) && login.optionalAction === 'FI_MIGRATION' && hasPendingFiMigrationDestination(login);

// Preserve the lazy dispatch getters: they copy the entry into the nested discovery dialog.
export const withFiMigrationEntry = (action: AggAction, entry: string): AggAction => {
  if (!isAnyFiMigrationAction(action)) {
    return action;
  }
  const result: AggAction = Object.defineProperties({}, Object.getOwnPropertyDescriptors(action));
  result.migrationEntry = entry;
  return result;
};

export const getFiMigrationProvenance = (login: InstitutionLogin): FiMigrationProvenance => ({
  institution_id: login.institutionId || 'unknown',
  institution_name: login.name,
  aggregator: getAggregatorFromChannel(login.channel || '') || 'unknown',
  from_channel: login.channel || 'unknown',
  to_channel: login.fiMigrationInfo?.toChannel || 'unknown',
  to_institution_id: login.fiMigrationInfo?.toInstitutionId || 'unknown',
});

// An optional destination can differ from the institution's default provider, even when its ID is unchanged.
export const getFiMigrationAggregator = ({
  mode,
  institutionLogin,
}: {
  mode: AccountDiscoveryMode;
  institutionLogin?: InstitutionLogin | null;
}): string | undefined => {
  const channel = institutionLogin?.fiMigrationInfo?.toChannel;
  return mode === 'MIGRATE-INSTITUTION-LOGIN' && channel && channel !== 'unknown'
    ? getAggregatorFromChannel(channel) || undefined
    : undefined;
};

export const getFiMigrationFunnelProperties = ({
  mode,
  institutionLogin,
  aggAction,
  autoMigration = false,
}: {
  mode: AccountDiscoveryMode;
  institutionLogin?: InstitutionLogin | null;
  aggAction?: AggAction | null;
  autoMigration?: boolean;
}): FiMigrationFunnelProperties => {
  if (mode !== 'MIGRATE-INSTITUTION-LOGIN' || !institutionLogin) {
    return {};
  }
  const { aggregator: _sourceAggregator, ...provenance } = getFiMigrationProvenance(institutionLogin);
  return {
    ...provenance,
    migration_entry: autoMigration ? 'auto migrate on error' : aggAction?.migrationEntry || 'unknown',
  };
};
